import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import Measure from "react-measure";
import {
	DataGrid,
	ProgressIndicator
} from "rhombus-react";
import { Libraries, loadDistincts } from "reducers/Libraries";
import { LIBRARY_COLUMNS } from "widgets/grid/columns/Libraries";

import { useGridScrollUtils } from "utils/DataGrid";
import { MIN_GRID_HEIGHT, DEFAULT_ROW_HEIGHT } from "utils/constants";

const distinctColumns = LIBRARY_COLUMNS
	.filter(v => /checkbox|range/gi.test(v.filter))
	.map(v => v.key)
	;

function mapLibraries(libraries){
	return libraries.items.map((library) => {
		/*
			We want some columns to have things like actions associated with a given library,
			rather than represent just a value on the library object.

			We can just pass the id here, so it can be accessed as `props.value` in the DataGrid formatter.
		*/
		return {
			...library,
			actions: library.id,
		};
	});
}

export default function Grid({ libraries = [], sort = "", isLoading = false }){
	const dataGridRef = useGridScrollUtils([libraries]);
	const dispatch = useDispatch();

	const augmentedLibraries = useMemo(() => { return mapLibraries(libraries); }, [libraries]);
	const [sortColumn, sortDirection] = sort.split(":");

	function getLibraryRow(rowID){
		return augmentedLibraries[rowID];
	}

	const dataGridProps = {
		columns:    LIBRARY_COLUMNS,
		rowGetter:  getLibraryRow,
		rowsCount:  libraries.items.length,
		rowHeight:  DEFAULT_ROW_HEIGHT,

		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(Libraries.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	useEffect(() => {
		dispatch(loadDistincts(distinctColumns));
	}, []);

	return (
		<Measure>
			{(obj) => {
				let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
				return (
					<div className="measure-component" ref={obj.measureRef}>
						<ProgressIndicator isLoading={isLoading} />
						<div ref={dataGridRef}>
							<DataGrid {...dataGridProps} minHeight={gridHeight} />
						</div>
					</div>
				);
			}}
		</Measure>
	);
}

Grid.displayName = "LibraryGrid";
